import { type KeyboardEvent, useRef } from 'react'

const getNextTabbable = (el: HTMLElement | null) => {
  if (!el) return null
  const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  const all = Array.from(document.querySelectorAll<HTMLElement>(selector))
  const idx = all.indexOf(el)
  if (idx < 0) return null
  return all[idx + 1] ?? null
}

export const useTypeaheadKeyboardNav = (resultCount: number) => {
  const refInput = useRef<HTMLInputElement>(null)
  const refButtons = useRef<(HTMLElement | null)[]>([])

  const focusIndex = (i: number) => {
    if (i >= 0 && i < resultCount) refButtons.current[i]?.focus()
    else requestAnimationFrame(() => (i < 0 ? refInput.current?.focus() : getNextTabbable(refInput.current))?.focus())
  }

  const onKeyDownInput = (e: KeyboardEvent<HTMLInputElement>) => {
    const isArrowDown = e.key === 'ArrowDown'
    const isTab = e.key === 'Tab'
    const isShift = e.shiftKey

    const first = resultCount > 0 ? refButtons.current[0] : null
    if ((isArrowDown || (isTab && !isShift)) && first) {
      e.preventDefault()
      requestAnimationFrame(() => first.focus())
    }
  }

  const onKeyDownButton = (i: number, e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      focusIndex(-1)
      return
    }

    const isTab = e.key === 'Tab'
    const isBackward = e.key === 'ArrowUp' || (isTab && e.shiftKey)
    const isForward = e.key === 'ArrowDown' || (isTab && !e.shiftKey)
    if (!isBackward && !isForward) return

    const next = isBackward ? i - 1 : i + 1

    if (isTab && next >= 0 && next < resultCount) return
    if (isTab && next >= resultCount && !getNextTabbable(refInput.current)) return

    e.preventDefault()
    focusIndex(next)
  }

  const setRefButton = (i: number, element: HTMLElement | null) => {
    refButtons.current[i] = element
  }

  return { onKeyDownButton, onKeyDownInput, refInput, setRefButton }
}
